import { getSupabaseClient } from './supabase';
import { AppError } from '@/utils/appErrors';

export type TimeTrackingContext = {
  location_id: string;
  location_name: string;
  timezone: string;
  reminders_enabled: boolean;
  can_manage_settings: boolean;
  can_add_entries?: boolean;
  can_view_active?: boolean;
};
export type TimeJob = { id: string; kind: 'installation' | 'service'; code: string; name: string; status: string; ticket_type?: string | null };
/** Job list views: asset-repair tickets list separately but still clock in as kind 'service'. */
export type TimeJobListKind = TimeJob['kind'] | 'asset_service';
export type WorkTypePart = { work_type: string | null; start_seconds: string; end_seconds: string | null };
export type TimeEntry = {
  entry_id: string; profile_id: string; person_name: string;
  job_kind: TimeJob['kind']; job_id: string; job_code: string; job_name: string;
  clock_in: string; clock_out: string | null; lunch_minutes: number | null;
  updated_at: string; can_edit: boolean; can_delete?: boolean;
  /** The recorded shift, or an unambiguous plan while the clock is running. */
  shift_id?: string | null; planned_starts_at?: string | null; planned_ends_at?: string | null;
  installation_room_id?: string | null; installation_labor_line_id?: string | null;
  installation_labor_purpose?: string | null;
  paid_seconds?: string | null; work_type_parts?: WorkTypePart[];
  allocation_label?: string; can_allocate?: boolean; needs_allocation?: boolean;
  /** Server-derived eligibility: installation or external service, with job view access. */
  photo_enabled?: boolean;
};
export type TimeHistory = { entries: TimeEntry[]; active: TimeEntry | null; has_more: boolean };
export type JobTimeHistory = { entries: Array<TimeEntry & { timezone: string }>; has_more: boolean; worked_minutes: number; open_count: number; all_people: boolean };
export type PlannedTimeShift = {
  shift_id: string; starts_at: string; ends_at: string | null; planned_minutes: number | null;
  person_name: string; publish_status: 'draft' | 'published'; required_people: number; timezone: string;
};
export type JobPlannedTime = { shifts: PlannedTimeShift[]; has_more: boolean };
export type ActiveTimeEntry = TimeEntry;
export type ActiveTimeOverview = { entries: ActiveTimeEntry[]; server_now: string };
export type TimePerson = { id: string; name: string };
export type ManualTimeEntry = {
  profileId: string; job: TimeJob; clockIn: string; clockOut: string; lunch: number; reason: string;
};

export const canManageTimeEntries = (context: TimeTrackingContext | null | undefined): boolean =>
  Boolean(context && (context.can_view_active ?? context.can_manage_settings));

async function rpc<T>(name: string, params?: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const request = getSupabaseClient().rpc(name, params);
  const { data, error } = await (signal ? request.abortSignal(signal) : request);
  if (error) throw error;
  return data as T;
}

export const fetchTimeShiftEntry = (shiftId: string) => rpc<TimeEntry>('schedule_time_shift_entry', { p_shift_id: shiftId });
export const fetchJobTimeHistory = (kind: TimeJob['kind'], jobId: string, before: string | null) =>
  rpc<JobTimeHistory>('job_time_history', { p_kind: kind, p_job_id: jobId, p_before: before });
export const fetchJobPlannedTime = (kind: TimeJob['kind'], jobId: string, after: string | null) =>
  rpc<JobPlannedTime>('job_planned_time', { p_kind: kind, p_job_id: jobId, p_after: after });
export const fetchTimeTrackingContext = () => rpc<TimeTrackingContext | null>('schedule_time_context');
export const fetchManualTimePeople = (locationId: string, search: string, after: string | null) =>
  rpc<{ people: TimePerson[]; has_more: boolean }>('schedule_time_manual_people', { p_location_id: locationId, p_search: search, p_after: after });
export const fetchManualTimeJobs = (locationId: string, kind: TimeJobListKind, search: string, after: string | null) =>
  rpc<{ jobs: TimeJob[]; has_more: boolean }>('schedule_time_manual_jobs', { p_location_id: locationId, p_kind: kind, p_search: search, p_after: after });
export const addTimeEntry = (entryId: string, locationId: string, draft: ManualTimeEntry) =>
  rpc<TimeEntry>('schedule_time_add', { p_entry_id: entryId, p_location_id: locationId, p_profile_id: draft.profileId,
    p_job_kind: draft.job.kind, p_job_id: draft.job.id, p_clock_in: draft.clockIn, p_clock_out: draft.clockOut,
    p_lunch_minutes: draft.lunch, p_reason: draft.reason });
export const fetchActiveTimeEntries = (locationId: string) => rpc<ActiveTimeOverview>('schedule_time_active', { p_location_id: locationId });
export const fetchTimeJobs = (locationId: string, search: string, after: string | null, kind: TimeJobListKind = 'installation') =>
  rpc<{ jobs: TimeJob[]; has_more: boolean }>('schedule_time_jobs_by_kind', { p_location_id: locationId, p_kind: kind, p_search: search, p_after: after });
/** A job the person has a shift on today at the site: Time tracking's "Scheduled for you today" (2026-10-28). */
export type ScheduledTimeJob = TimeJob & { starts_at: string; ends_at: string | null };
export const fetchScheduledTimeJobs = (locationId: string) =>
  rpc<{ jobs: ScheduledTimeJob[] }>('schedule_time_scheduled_jobs', { p_location_id: locationId });
export const fetchTimeHistory = (locationId: string, start: string, end: string, before: string | null) =>
  rpc<TimeHistory>('schedule_time_history', { p_location_id: locationId, p_start: start, p_end: end, p_before: before });
export const clockIn = (entryId: string, job: TimeJob, locationId: string, workType?: string) =>
  job.kind === 'installation' && workType
    ? rpc<TimeEntry>('installation_time_clock_in_with_work_type', { p_entry_id: entryId, p_job_id: job.id, p_location_id: locationId, p_work_type: workType })
    : rpc<TimeEntry>('schedule_time_clock_in', { p_entry_id: entryId, p_job_kind: job.kind, p_job_id: job.id, p_location_id: locationId });
export const fetchInstallationClockWorkTypes = (jobId: string, locationId: string) =>
  rpc<{ work_types: string[]; suggested: string; basis: string }>('installation_clock_in_work_type_options', { p_job_id: jobId, p_location_id: locationId });
export function timeEntryJobName(entry: Pick<TimeEntry, 'job_name' | 'job_code'>): string {
  const terminal = entry.job_name.match(/\s*\(([^()]*)\)\s*$/);
  const identifiers = terminal?.[1].split('/').map(value => value.trim()) ?? [];
  return terminal && identifiers.includes(entry.job_code) && identifiers.every(value => /^[A-Z]+\d+(?:-\d+)?$/.test(value))
    ? entry.job_name.slice(0, terminal.index).trimEnd() : entry.job_name;
}
export type InstallationTimeOptions = {
  entry: TimeEntry; rooms: Array<{ id: string; name: string }>;
  buckets: Array<{ id: string; room_id: string | null; position: string | null }>;
  purposes: string[];
};
export const fetchInstallationTimeOptions = (entryId: string) =>
  rpc<InstallationTimeOptions>('installation_time_allocation_options', { p_entry_id: entryId });
export async function allocateInstallationTime(entry: TimeEntry, purpose: string | null, reason: string): Promise<TimeEntry> {
  try {
    return await rpc<TimeEntry>('installation_time_allocate_purpose', { p_entry_id: entry.entry_id, p_expected_updated_at: entry.updated_at,
      p_purpose: purpose, p_reason: reason });
  } catch (error) {
    const failure = error as { code?: string; message?: string };
    if (failure?.code === 'PT409') throw new AppError('conflict', 'This time entry changed. Refresh the work-type options and try again.', { code: 'PT409', status: 409, cause: error });
    const reasons: Record<string, string> = {
      'Choose a room on this installation.': 'The room is no longer available on this installation. Refresh the options and choose another room.',
      'Choose a labor bucket on the current installation quote.': 'The labor bucket changed. Refresh the options and choose a bucket on the current quote.',
      'The room must match the selected labor bucket.': 'Choose the room that belongs to the selected labor bucket.',
      'This time already belongs to its planned visit.': 'This time already belongs to its planned visit. Refresh the recorded time list.',
      'Enter a reason for allocating this recorded time.': 'Enter a reason for allocating this recorded time.',
      'Keep the reason under 2,000 characters.': 'Keep the reason under 2,000 characters.',
    };
    if (failure?.code === '22023' && reasons[failure.message ?? '']) throw new AppError('validation', reasons[failure.message!], { code: failure.code, cause: error });
    throw error;
  }
}
export const clockOut = (entryId: string, lunchMinutes: number, note = '') =>
  rpc('schedule_time_clock_out_with_note', { p_entry_id: entryId, p_lunch_minutes: lunchMinutes, p_note: note });
export const editTimeEntry = (entry: TimeEntry, clockInAt: string, clockOutAt: string | null, lunch: number | null, reason: string, parts: WorkTypePart[] | null = null) =>
  rpc(entry.job_kind === 'installation' ? 'installation_time_edit_work_types' : 'schedule_time_edit', { p_entry_id: entry.entry_id, p_expected_updated_at: entry.updated_at,
    p_clock_in: clockInAt, p_clock_out: clockOutAt, p_lunch_minutes: lunch, p_reason: reason, ...(entry.job_kind === 'installation' ? { p_parts: parts } : {}) });
export const setTimeReminders = (locationId: string, enabled: boolean) =>
  rpc('schedule_time_set_reminders', { p_location_id: locationId, p_enabled: enabled });

export function workedMinutes(entry: Pick<TimeEntry, 'clock_in' | 'clock_out' | 'lunch_minutes'>): number | null {
  if (!entry.clock_out) return null;
  return Math.max(0, (Date.parse(entry.clock_out) - Date.parse(entry.clock_in)) / 60_000 - (entry.lunch_minutes ?? 0));
}

/** Actual clock-out wins; an active linked clock shows its plan only until that end. */
export function actualTimeDisplayEnd(
  clockOutAt: string | null | undefined,
  scheduledEndAt: string | null | undefined,
  now: number,
): string | null {
  if (clockOutAt) return Number.isFinite(Date.parse(clockOutAt)) ? clockOutAt : null;
  if (!scheduledEndAt || !Number.isFinite(now)) return null;
  const scheduledEnd = Date.parse(scheduledEndAt);
  return Number.isFinite(scheduledEnd) && scheduledEnd > now ? scheduledEndAt : null;
}

export function elapsedClockTime(clockInAt: string, now: number): string {
  const elapsed = now - Date.parse(clockInAt);
  if (!Number.isFinite(elapsed)) return '—';
  const seconds = Math.max(0, Math.floor(elapsed / 1000));
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
    .map(value => String(value).padStart(2, '0')).join(':');
}

export const voidTimeEntry = (entry: TimeEntry, reason: string) => rpc('schedule_time_void', {
  p_entry_id: entry.entry_id, p_expected_updated_at: entry.updated_at, p_reason: reason,
});
export type TimeLaunchState = { context: TimeTrackingContext; active: TimeEntry | null; can_clock_in_home: boolean };
export const fetchTimeLaunchState = () => rpc<TimeLaunchState | null>('schedule_time_launch_state');


export type TimeReportEntry = TimeEntry & { timezone: string; partial_range: boolean; provisional_elapsed_seconds: string | null };
export type TimeReportPlan = {
  shift_id: string; profile_id: string; person_name: string; job_kind: 'installation' | 'service' | 'event' | 'independent';
  job_id: string; job_code: string; job_name: string; starts_at: string; ends_at: string | null;
  break_minutes: number; work_type: string | null; scheduled_seconds: string | null; partial_range: boolean;
};
export type TimeReport = {
  entries: TimeReportEntry[]; plans: TimeReportPlan[]; start: string | null; end: string | null; timezone: string;
  all_time: boolean; jobs: string[];
  generated_at: string; complete: true; range_policy: string;
};
export const fetchTimeReport = (range: {locationId: string; start: string | null; end: string | null; timeZone: string; profileId: string | null; jobKind: string | null; jobs: string[]; allTime: boolean}) =>
  rpc<TimeReport>('schedule_time_report_filtered', {p_location_id: range.locationId, p_start: range.start, p_end: range.end,
    p_timezone: range.timeZone, p_profile_id: range.profileId, p_job_kind: range.jobKind, p_jobs: range.jobs, p_all_time: range.allTime});

export type TimeReportJob = { id: string; kind: 'installation' | 'service' | 'event' | 'independent'; code: string; name: string };
export type TimeReportJobs = { jobs: TimeReportJob[]; selected: TimeReportJob[]; has_more: boolean; next: string | null };
export const fetchTimeReportJobs = (locationId: string, search: string, kind: string | null, after: string | null, selected: string[], signal?: AbortSignal) =>
  rpc<TimeReportJobs>('schedule_time_report_jobs', {p_location_id: locationId, p_search: search, p_kind: kind, p_after: after, p_selected: selected}, signal);
